import { action, makeObservable, observable } from 'mobx'
import * as R from 'ramda'

class QuestionImportExportStore {
    @observable isLoading = true

    clients = []

    @observable selectedClient = {}

    @observable details = {
        type: '',
        msg: '',
        url: '',
    }

    @observable file

    constructor(store) {
        this.appStore = store
        makeObservable(this)
    }

    @action
    loadClients = async () => {
        const response = await this.appStore.http.get('/client')
        this.clients = response.clients.map(({ clientId, name }) => ({
            label: `${name} [${clientId}]`,
            value: Number(clientId),
        }))

        this.selectedClient = this.clients.find(({ value }) => value === 1)
    }

    setDetails = (type = '', msg = '', url = '') => {
        this.details = {
            type,
            msg,
            url,
        }
    }

    @action
    init = async () => {
        const { appStore } = this
        try {
            await this.loadClients()
            this.setDetails()
            this.isLoading = false
        } catch (e) {
            appStore.addNotification(null, 'Error initiating import / export tool', 'error')
        }
    }

    changeClient = (obj) => {
        this.selectedClient = obj
    }

    changeUploadFile = (e) => {
        const { appStore } = this
        this.file = undefined

        const [file] = e.target.files
        const { name, type } = file

        if (type !== 'text/csv') {
            appStore.addNotification(null, `${name} (${type}) is not CSV`, 'error')
        } else {
            this.file = file
        }
    }

    getSocket = (query, onConnect, onResponse, onError) => {
        const { appStore } = this
        const httpUrl = appStore.http.gatewayUrl
        const socketUrl = httpUrl.replace(/https:\/\//, 'wss://').replace(/http:\/\//, 'ws://')

        const socket = io(socketUrl, {
            query,
        })
        socket.on('connection', onConnect)
        socket.on('response', onResponse)
        socket.on('error', onError)
        socket.on('connect_failed', onError)

        return socket
    }

    wsDisconnect = () => {
        try {
            this.socket.disconnect()
        } catch (e) {}

        Reflect.deleteProperty(this, 'socket')
        this.file = undefined
        this.isLoading = false
    }

    onSocketError = (error) => {
        this.wsDisconnect()
        if (error) {
            console.error(error)
        }

        this.setDetails('error', 'Error completing action')
    }

    onDownloadConnect = () => {
        const { appStore, selectedClient } = this
        const { userData } = appStore
        const { userId, sessionId } = userData
        const request = {
            kind: 'questionLangExport',
            clientId: selectedClient.value,
            userId,
            sessionId,
        }
        this.socket.emit('data', JSON.stringify(request))
    }

    onDownloadResponse = (fileUrl) => {
        this.wsDisconnect()
        this.setDetails('success', 'Question download complete', fileUrl)
    }

    downloadQuestions = () => {
        const { appStore } = this

        this.isLoading = true

        let query = '&target=export'
        query += '&method=questionLangs'
        query += `&token=${appStore.token}`

        this.setDetails()
        this.socket = this.getSocket(
            query,
            this.onDownloadConnect,
            this.onDownloadResponse,
            this.onSocketError,
        )
    }

    onImportResponse = (response) => {
        const { appStore, selectedClient, allowEditingDefaultLocale } = this
        const { userData } = appStore
        const { userId } = userData

        const json = JSON.parse(response)
        const { msg, detail } = json
        if (msg === 'Done' && this.details.type !== 'error') {
            this.wsDisconnect()
            this.setDetails('success', 'Question import complete')
        } else if (json.msg === 'Uploaded') {
            this.socket.emit('data', JSON.stringify({
                clientId: selectedClient.value,
                allowEditingDefaultLocale: R.or(allowEditingDefaultLocale, false),
                userId,
            }))
        } else if (msg === 'Error') {
            this.wsDisconnect()
            if (!R.isNil(detail)) {
                this.setDetails('error', `Error importing question - ${detail}`)
            }
        }
    }

    onImportConnect = () => {
        this.socket.emit('data', this.file)
    }

    uploadQuestions = () => {
        const { appStore } = this

        let query = '&target=import'
        query += '&method=questionLangImport'
        query += `&token=${appStore.token}`

        this.isLoading = true

        this.setDetails()
        this.socket = this.getSocket(
            query,
            this.onImportConnect,
            this.onImportResponse,
            this.onSocketError,
        )
    }
}

export default QuestionImportExportStore
